import { useCallback } from 'react'
import { FieldValues, Resolver } from 'react-hook-form'
import * as yup from 'yup'

export type ValidationSchemaProps = yup.AnyObjectSchema

interface IYupValidationResolverType<T> {
  schemaFunction?: (data: T) => ValidationSchemaProps
  validationSchema?: ValidationSchemaProps
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type IYupValidationResolverReturn<T> = Resolver<T, any>

export function useYupValidationResolver<T = FieldValues>({
  schemaFunction,
  validationSchema,
}: IYupValidationResolverType<T>): IYupValidationResolverReturn<T> {
  return useCallback(
    async (data: T) => {
      try {
        let customValidationSchema: ValidationSchemaProps

        if (schemaFunction) {
          customValidationSchema = schemaFunction(data)
        }

        if (!schemaFunction) {
          customValidationSchema = validationSchema
        }

        const values = await customValidationSchema.validate(data, {
          abortEarly: false,
        })

        return {
          values,
          errors: {},
        }
      } catch (errors) {
        return {
          values: {},
          errors: errors?.inner?.reduce(
            (allErrors, currentError) => ({
              ...allErrors,
              [currentError.path || currentError.type || 'validation']: {
                type: currentError.type ?? 'validation',
                message: currentError.message,
              },
            }),
            {},
          ),
        }
      }
    },
    [schemaFunction, validationSchema],
  )
}
